<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChatConversationDetail } from '@/services/chat/chatHttp.service'
import { chatApi } from '@/services/api'
import BModal from '@/components/common/BModal.vue'
import ResourceImage from '@/components/resource/resourceImage.vue'
import Loading from '@/components/common/loading.vue'

const props = defineProps<{
  conversationId: string | null
}>()

const open = defineModel<boolean>({ default: false })

const { t } = useI18n()
const isLoading = ref(false)
const members = ref<ChatConversationDetail['members']>([])

const loadMembers = async () => {
  if (!props.conversationId) return
  isLoading.value = true
  try {
    const detail = await chatApi.getConversation(props.conversationId).promise
    members.value = detail.members || []
  }
  catch (err) {
    console.error('Failed to load conversation members', err)
    members.value = []
  }
  finally {
    isLoading.value = false
  }
}

watch(open, (isOpen) => {
  if (isOpen) loadMembers()
  else members.value = []
})
</script>

<template>
  <BModal
    v-model="open"
    data-cy="chat-members-modal"
  >
    <template #header>
      <h2
        class="font-semibold"
        data-cy="chat-members-title"
      >
        {{ t('modules.chat.members') }}
      </h2>
    </template>
    <div class="min-h-24 max-h-[80vh] overflow-y-auto mini-scroll">
      <Loading
        v-if="isLoading"
        class="py-6"
      />
      <div
        v-else-if="!members.length"
        class="py-6 text-center text-sm opacity-60"
        data-cy="chat-members-empty"
      >
        {{ t('modules.chat.emptyMembers') }}
      </div>
      <ul
        v-else
        class="flex flex-col gap-1"
        data-cy="chat-members-list"
      >
        <li
          v-for="member in members"
          :key="member.resourceId"
          class="flex items-center gap-3 px-2 py-2 rounded-lg"
          :data-cy="`chat-member-${member.resourceId}`"
        >
          <ResourceImage
            :resource="{
              _id: member.resourceId,
              firstname: member.firstname,
              lastname: member.lastname,
              picture: member.picture,
            }"
            width="w-9"
            height="h-9"
            disable-link
          />
          <div class="min-w-0 flex-1">
            <div class="text-sm font-medium truncate">
              {{ `${member.firstname || ''} ${member.lastname || ''}`.trim() || '—' }}
            </div>
            <div
              v-if="member.role === 'admin'"
              class="text-[11px] opacity-60"
            >
              {{ t('modules.chat.roleAdmin') }}
            </div>
          </div>
        </li>
      </ul>
    </div>
  </BModal>
</template>
